import { Injectable } from '@angular/core';
import { SETUP_PHASES_SEED, SETUP_STEPS_SEED } from './prototype/setup-data';

import {
  AccessMatrixRow,
  ActivityCatalogItem,
  CatalogPosition,
  EquipmentAsset,
  SetupPhase,
  SetupStep,
  OperatingSystem,
  NewHire,
  OnboardingDoc,
  Opportunity,
  OppStage,
  PacketReference,
  Persona,
  PersonaLadder,
  PersonRow,
  Pathway,
  ProjectAttributes,
  Project,
  Role,
  Subcontractor,
  TimeOffRequest,
  TrainingData,
  UserAccount,
} from '../models';
import { DataRepository } from './data-repository';
import { PERSONA_LADDERS_SEED, POSITION_PATHWAYS_SEED } from './seed/career-pathways.seed';
import {
  ACCESS_MATRIX_SEED,
  POSITION_CATALOG_SEED,
  ROLES_SEED,
  USERS_SEED,
} from './seed/users-access.seed';
import { NEW_HIRES_SEED, ONBOARDING_DOCS_SEED } from './seed/onboarding.seed';
import { PEOPLE_SEED } from './seed/people.seed';
import { PACKET_REFERENCE_SEED } from './seed/packet.seed';
import { OPPORTUNITIES_SEED, OPP_STAGES_SEED } from './seed/opportunities.seed';
import { withSampleSchedules } from './seed/sample-schedules';
import { buildTimeOffRequests } from './seed/time-off.seed';
import { ACTIVITY_CATALOG_SEED, PROJECT_ATTRIBUTES_SEED } from './seed/project-settings.seed';
import { SUBCONTRACTORS_SEED } from './seed/subcontractors.seed';
import { TRAINING_SEED } from './seed/training.seed';
import { OPERATING_SYSTEMS_SEED } from './seed/operating-systems.seed';
import { PERSONAS_SEED } from './seed/personas.seed';
import { PROJECTS_SEED } from './seed/projects.seed';

// Bump this version whenever a seeded model's shape changes, so stale caches are
// re-seeded rather than read back with missing fields. v4: Persona gained the
// sidebar role fields (initials, tier, isCEO, isSupervisor, inOnboarding,
// directReports, hasCareerLadder). v5: Persona gained the packet display fields
// (dottedLine, teammate, mobilePrimary, note). v6 (projects only): work sessions
// and time entries are now generated into the seed (withSampleSchedules).
const KEY = {
  // v2: steps moved from a `phase` heading string to a `phaseId`. A v1 list
  // cached in a browser has neither, so every step would land in "Ungrouped" —
  // the key is bumped so the new shape seeds fresh rather than half-reading the
  // old one.
  setupSteps: 'dros.v2.setupSteps',
  setupPhases: 'dros.v1.setupPhases',
  divisions: 'dros.v6.divisions',
  personas: 'dros.v9.personas',
  operatingSystems: 'dros.v6.operatingSystems',
  projects: 'dros.v12.projects',
  positionPathways: 'dros.v5.positionPathways',
  personaLadders: 'dros.v6.personaLadders',
  payClasses: 'dros.v6.payClasses',
  certifications: 'dros.v1.certifications',
  equipmentTypes: 'dros.v1.equipmentTypesCatalog',
  equipmentAssets: 'dros.v1.equipmentAssets',
  softwareTools: 'dros.v1.softwareTools',
  employmentTypes: 'dros.v5.employmentTypes',
  companyLocations: 'dros.v6.companyLocations',
  users: 'dros.v5.users',
  people: 'dros.v6.people',
  newHires: 'dros.v5.newHires',
  positionCatalog: 'dros.v4.positionCatalog',
  accessMatrix: 'dros.v3.accessMatrix',
} as const;

/**
 * Browser-persistence implementation of {@link DataRepository}.
 *
 * Data is stored under versioned localStorage keys and seeded on first run.
 * All reads are defensive: a corrupt or missing key falls back to seed data
 * rather than throwing, so a bad cache never bricks the app.
 */
@Injectable()
export class LocalStorageRepository extends DataRepository {


  async listPersonas(): Promise<Persona[]> {
    return this.read<Persona>(KEY.personas, PERSONAS_SEED);
  }

  async listOperatingSystems(): Promise<OperatingSystem[]> {
    return this.read<OperatingSystem>(KEY.operatingSystems, OPERATING_SYSTEMS_SEED);
  }

  async listProjects(): Promise<Project[]> {
    // Work sessions and time entries are generated (never static seed data),
    // exactly as the prototype's withSampleSchedules did — from active projects
    // × personas across a today-relative window. Baked into the seed so Time
    // Tracking and the Scheduling views populate on first load.
    const seed = withSampleSchedules(
      structuredClone(PROJECTS_SEED) as Project[],
      PERSONAS_SEED.map((p) => ({ id: p.id, name: p.name })),
    );
    return this.read<Project>(KEY.projects, seed);
  }

  async saveProjects(projects: readonly Project[]): Promise<Project[]> {
    return this.write(KEY.projects, projects);
  }

  async listPositionPathways(): Promise<Pathway[]> {
    return this.read<Pathway>(KEY.positionPathways, POSITION_PATHWAYS_SEED);
  }

  async listPersonaLadders(): Promise<PersonaLadder[]> {
    return this.read<PersonaLadder>(KEY.personaLadders, PERSONA_LADDERS_SEED);
  }







  async saveEquipmentAssets(assets: readonly EquipmentAsset[]): Promise<EquipmentAsset[]> {
    return this.write(KEY.equipmentAssets, assets);
  }



  async listSetupSteps(): Promise<SetupStep[]> {
    return this.read<SetupStep>(KEY.setupSteps, SETUP_STEPS_SEED);
  }

  async listSetupPhases(): Promise<SetupPhase[]> {
    return this.read<SetupPhase>(KEY.setupPhases, SETUP_PHASES_SEED);
  }

  async listUsers(): Promise<UserAccount[]> {
    return this.read<UserAccount>(KEY.users, USERS_SEED);
  }

  async listRoles(): Promise<Role[]> {
    // Static reference data — served straight from seed (deep-cloned).
    return ROLES_SEED.map((r) => structuredClone(r));
  }

  async listAccessMatrix(): Promise<AccessMatrixRow[]> {
    return this.read<AccessMatrixRow>(KEY.accessMatrix, ACCESS_MATRIX_SEED);
  }

  async saveAccessMatrix(rows: readonly AccessMatrixRow[]): Promise<AccessMatrixRow[]> {
    return this.write(KEY.accessMatrix, rows);
  }

  async listPositionCatalog(): Promise<CatalogPosition[]> {
    return this.read<CatalogPosition>(KEY.positionCatalog, POSITION_CATALOG_SEED);
  }

  async savePositionCatalog(catalog: readonly CatalogPosition[]): Promise<CatalogPosition[]> {
    return this.write(KEY.positionCatalog, catalog);
  }

  async listPeople(): Promise<PersonRow[]> {
    return this.read<PersonRow>(KEY.people, PEOPLE_SEED);
  }

  async listTraining(): Promise<TrainingData> {
    // Nested object with session-only edits, so served straight from seed (deep-cloned).
    return structuredClone(TRAINING_SEED);
  }

  async listOnboardingDocs(): Promise<OnboardingDoc[]> {
    return ONBOARDING_DOCS_SEED.map((d) => structuredClone(d));
  }

  async listNewHires(): Promise<NewHire[]> {
    return this.read<NewHire>(KEY.newHires, NEW_HIRES_SEED);
  }

  async listPacketReference(): Promise<PacketReference> {
    return structuredClone(PACKET_REFERENCE_SEED);
  }

  async listOpportunities(): Promise<Opportunity[]> {
    return structuredClone(OPPORTUNITIES_SEED) as Opportunity[];
  }

  async listOppStages(): Promise<OppStage[]> {
    return structuredClone(OPP_STAGES_SEED) as OppStage[];
  }

  async listProjectAttributes(): Promise<ProjectAttributes> {
    return structuredClone(PROJECT_ATTRIBUTES_SEED);
  }

  async listActivityCatalog(): Promise<ActivityCatalogItem[]> {
    return structuredClone(ACTIVITY_CATALOG_SEED) as ActivityCatalogItem[];
  }


  async listSubcontractors(): Promise<Subcontractor[]> {
    return structuredClone(SUBCONTRACTORS_SEED) as Subcontractor[];
  }

  async listTimeOff(): Promise<TimeOffRequest[]> {
    // Generated with today-relative dates, matching the prototype initializer.
    return buildTimeOffRequests(PERSONAS_SEED.map((p) => ({ id: p.id, name: p.name })));
  }

  /** Read a collection, seeding (and persisting the seed) on first access. */
  private read<T>(key: string, seed: readonly T[]): T[] {
    const raw = localStorage.getItem(key);
    if (raw === null) {
      const seeded = seed.map((item) => structuredClone(item));
      this.persist(key, seeded);
      return seeded;
    }
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        throw new Error('Persisted value is not an array');
      }
      return parsed as T[];
    } catch (err) {
      console.warn(`Corrupt data at "${key}", falling back to seed.`, err);
      return seed.map((item) => structuredClone(item));
    }
  }

  /** Deep-clone, persist, and return a collection — so callers never share the stored reference. */
  private write<T>(key: string, value: readonly T[]): T[] {
    const next = value.map((item) => structuredClone(item));
    this.persist(key, next);
    return next;
  }

  /** Serialize a value to its localStorage key; logs (never throws) on quota/serialization failure. */
  private persist(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      // Quota or serialization failure — surfaced, never silently dropped.
      console.error(`Failed to persist "${key}"`, err);
    }
  }
}
